import ContractOverviewIcons from '@/icons/ContractOverviewIcons'
import { CustomerContract } from '@/modules/Contract/types'
import { formatDateWithFormatter } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Typogaphy } from 'vnetwork-ui'

type Props = { contract: CustomerContract }

const ContractCard = ({ contract }: Props) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    return (
        <div className="bg-white border rounded-lg shadow-sm text-sm">
            <div className="p-4 bg-[#F9FBFF] rounded-t-lg">
                <div className="flex xs:flex-col md:flex-row justify-between items-center">
                    <Link
                        to={generatePathDatacentral(paths.contractDetail, {
                            id: contract.contract_id,
                        })}
                        replace
                    >
                        <div className="flex gap-1 items-center">
                            <div className="w-4 h-4" color="primary">
                                <ContractOverviewIcons />
                            </div>
                            <Typogaphy className="text-primary">
                                {contract.contract_number_id ||
                                    `DC - HD - ${contract.contract_id}`}
                            </Typogaphy>
                        </div>
                    </Link>
                    <Typogaphy className="text-xs text-[#1E1E1E]">
                        {contract?.contract_expiry_date &&
                            formatDateWithFormatter(
                                contract?.contract_expiry_date
                            )}
                    </Typogaphy>
                </div>
            </div>
            <div className="p-4">
                <Typogaphy className="text-xs text-gray-500 capitalize">
                    {contract.company_industry ||
                        contract?.customer_industry ||
                        '---'}
                </Typogaphy>
                <Typogaphy className="mt-2 font-semibold uppercase">
                    {contract?.company_name || contract?.customer_name || '---'}
                </Typogaphy>
                <Typogaphy className="mt-1 text-sm">
                    {contract?.company_name
                        ? contract.customer_name
                        : contract?.customer_position || '---'}
                </Typogaphy>
                {/* Services Section */}
                {contract?.services && contract?.services?.length > 0 && (
                    <div className="mt-3">
                        <Typogaphy className="text-xs text-gray-500 mb-2">
                            {t('services')} ({contract?.services?.length})
                        </Typogaphy>
                        <div className="space-y-1 max-h-20 overflow-y-auto">
                            {contract?.services
                                ?.slice(0, 3)
                                ?.map((service, index) => (
                                    <div
                                        key={service.service_id || index}
                                        className="text-xs bg-gray-50 p-2 rounded"
                                    >
                                        <div className="font-medium text-gray-700 truncate">
                                            {service.service_name}
                                        </div>
                                        <div className="text-gray-500 mt-0.5">
                                            {service.product_group_name} •{' '}
                                            {service.service_status}
                                        </div>
                                    </div>
                                ))}
                            {contract?.services?.length > 3 && (
                                <div className="text-xs text-gray-500 text-center py-1">
                                    +{contract?.services?.length - 3}{' '}
                                    {t('more-services')}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div
                    className={`mt-2 flex xs:flex-col flex-row items-center ${contract?.services && contract?.services?.length > 1 ? 'justify-between' : 'justify-end'}`}
                >
                    {contract?.services && contract?.services?.length > 1 && (
                        <div
                            className={`rounded-md border-none text-sm font-medium capitalize px-2.5 py-2`}
                        >
                            {contract?.services
                                .flatMap(
                                    (service) => service?.product_group_name
                                )
                                .join(', ')
                                .toLowerCase() || '---'}
                        </div>
                    )}
                    <div
                        onClick={() => {
                            navigate(
                                generatePathDatacentral(paths.contractDetail, {
                                    id: contract.contract_id,
                                })
                            )
                        }}
                    >
                        <Typogaphy className="text-xs text-[#6F6F6F] hover:underline hover:text-[#1a237e] cursor-pointer">
                            {t('view-detail')}
                        </Typogaphy>
                    </div>
                </div>
            </div>
        </div>
    )
}

export const ContractCardSkeleton = () => {
    return (
        <div className="bg-white border rounded-lg shadow-sm text-sm animate-pulse">
            <div className="p-4 bg-[#F9FBFF] rounded-t-lg">
                <div className="flex xs:flex-col md:flex-row justify-between items-center">
                    <div className="flex gap-2 items-center">
                        <div className="w-4 h-4 border rounded-full flex justify-center items-center">
                            <div className="w-4 h-4 bg-gray-300 rounded-full" />
                        </div>
                        <div className="h-4 w-32 bg-gray-300 rounded" />
                    </div>
                    <div className="h-4 w-20 bg-gray-300 rounded mt-2 md:mt-0" />
                </div>
            </div>
            <div className="p-4">
                <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
                <div className="h-4 w-48 bg-gray-300 rounded mb-2" />
                <div className="h-3 w-32 bg-gray-200 rounded mb-4" />
                <div className="mt-2 flex xs:flex-col flex-row justify-between items-center">
                    <div className="h-5 w-20 bg-gray-300 rounded-md" />
                    <div className="h-4 w-16 bg-gray-300 rounded mt-2 xs:mt-2 md:mt-0" />
                </div>
            </div>
        </div>
    )
}

export default ContractCard
